/**
 * Featured Info Bar — seamless infinite marquee.
 *
 * Technique (single track, no overlap, no gaps, no drift):
 *   1. Repeat the items inside ONE track until it is at least as wide as the
 *      container (guarantees the strip fills the viewport).
 *   2. Duplicate the whole filled set once, so the track is [setA][setA copy].
 *   3. CSS animates the track translateX(0 -> -50%). Because -50% is exactly
 *      one copy wide (the gap is margin-right on each item, so it is part of
 *      the measured width), the duplicate lands precisely where the original
 *      began — a perfect seamless loop.
 *
 * Duration is derived from the track width and the speed setting, so the
 * visual speed (px/sec) stays consistent regardless of how many items there
 * are or how many copies were needed to fill the bar.
 */
class InfoMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.featured-info-bar__track');
    if (!this.track) return;

    // Original item markup, kept so we can rebuild cleanly on resize.
    this.baseHTML = this.track.innerHTML;
    this.speed = parseFloat(this.dataset.speed) || 4;

    this.build();

    // Rebuild after images load (icon widths change measurements) and on resize.
    window.addEventListener('load', () => this.build());
    this.resizeObserver = new ResizeObserver(this.debounce(() => this.build(), 200));
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  build() {
    const track = this.track;

    // Reset to the original single set.
    track.innerHTML = this.baseHTML;

    const baseItems = Array.from(track.children);
    if (baseItems.length === 0) return;

    const containerWidth = this.offsetWidth || window.innerWidth;

    // 1. Repeat items until the set is at least as wide as the container.
    let guard = 0;
    while (track.scrollWidth < containerWidth && guard < 100) {
      baseItems.forEach((node) => track.appendChild(node.cloneNode(true)));
      guard += 1;
    }

    // Width of ONE filled set (before duplicating).
    const setWidth = track.scrollWidth;

    // 2. Duplicate the whole filled set once for the seamless second half.
    Array.from(track.children).forEach((node) => {
      const clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    // 3. Derive duration for a consistent px/sec speed.
    //    speed 1..10 -> ~25..250 px/sec.
    const pxPerSecond = this.speed * 25;
    const duration = Math.max(4, setWidth / pxPerSecond);
    this.style.setProperty('--info-bar-duration', `${duration}s`);

    this.classList.add('is-ready');
  }

  debounce(fn, wait) {
    let t;
    return () => {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }
}

if (!customElements.get('info-marquee')) {
  customElements.define('info-marquee', InfoMarquee);
}
